/**
 * One series over a number line, the dataviz way: one y-axis from zero, a 2px line in the accent, hairline grid,
 * labelled vertical markers for the steps that matter, an optional reference line (a price), and the selected point
 * ringed and labelled. A crosshair finds the nearest x on hover, and arrow keys walk it, with a tooltip that leads with
 * the value. The page keeps a table of the same numbers, so nothing is hover-only.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import s from './LineChart.module.css'

export interface LinePoint {
  x: number
  y: number
}

export interface LineMarker {
  x: number
  label: string
  /** Drawn quieter: a step the model names but doesn't price. */
  muted?: boolean
}

const PAD = { top: 34, right: 18, bottom: 28, left: 52 }

/** A round step for about four y ticks. */
function niceStep(max: number): number {
  const raw = max / 4
  const mag = 10 ** Math.floor(Math.log10(raw || 1))
  const n = raw / mag
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag
}

function useWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(fallback)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([e]) => e && setWidth(Math.max(240, Math.round(e.contentRect.width))))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return { ref, width }
}

export function LineChart({
  label,
  seriesLabel,
  points,
  formatX,
  formatY,
  formatTick = formatY,
  xTicks,
  markers = [],
  refLine,
  selected,
  height = 280,
}: {
  /** What the chart shows, for screen readers and as the group's name. */
  label: string
  /** The series' name in the tooltip. */
  seriesLabel: string
  points: LinePoint[]
  formatX: (x: number) => string
  formatY: (y: number) => string
  /** The y-axis ticks, when they read better rounder than the values ("$60" against "$53.17"). */
  formatTick?: (y: number) => string
  xTicks?: number[]
  markers?: LineMarker[]
  refLine?: { y: number; label: string }
  /** The x of the point the rest of the page is about: ringed and labelled. */
  selected?: number
  height?: number
}) {
  const { ref, width } = useWidth(640)
  const [hover, setHover] = useState<number | null>(null)

  const g = useMemo(() => {
    const xs = points.map((p) => p.x)
    const x0 = Math.min(...xs)
    const x1 = Math.max(...xs)
    const top = Math.max(...points.map((p) => p.y), refLine?.y ?? 0) * 1.08 || 1
    const step = niceStep(top)
    const yMax = Math.ceil(top / step) * step
    const iw = width - PAD.left - PAD.right
    const ih = height - PAD.top - PAD.bottom
    const sx = (x: number) => PAD.left + (x1 === x0 ? 0 : ((x - x0) / (x1 - x0)) * iw)
    const sy = (y: number) => PAD.top + ih - (y / yMax) * ih
    const yTicks = Array.from({ length: Math.round(yMax / step) + 1 }, (_, i) => i * step)
    const path = points.map((p, i) => `${i ? 'L' : 'M'}${sx(p.x).toFixed(1)} ${sy(p.y).toFixed(1)}`).join(' ')
    return { x0, x1, sx, sy, yTicks, path, iw, ih }
  }, [points, refLine, width, height])

  const nearest = (clientX: number, el: Element) => {
    const r = el.getBoundingClientRect()
    const x = g.x0 + ((clientX - r.left - PAD.left) / g.iw) * (g.x1 - g.x0)
    let best = 0
    points.forEach((p, i) => {
      if (Math.abs(p.x - x) < Math.abs(points[best]!.x - x)) best = i
    })
    return best
  }
  const onMove = (e: PointerEvent<SVGRectElement>) => setHover(nearest(e.clientX, e.currentTarget.ownerSVGElement ?? e.currentTarget))
  const selIndex = selected === undefined ? -1 : points.findIndex((p) => p.x === selected)
  const onKey = (e: KeyboardEvent) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : e.key === 'PageUp' ? 10 : e.key === 'PageDown' ? -10 : 0
    if (!step) return
    e.preventDefault()
    setHover((h) => Math.max(0, Math.min(points.length - 1, (h ?? Math.max(0, selIndex)) + step)))
  }

  const hp = hover === null ? null : points[hover]
  const sp = selIndex >= 0 ? points[selIndex] : null
  const tipLeft = hp ? Math.min(Math.max(g.sx(hp.x) + 12, 0), width - 170) : 0
  const ticks = xTicks ?? [g.x0, g.x1]

  return (
    <div
      ref={ref}
      className={s.chart}
      role="group"
      aria-label={label}
      tabIndex={0}
      onKeyDown={onKey}
      onFocus={() => setHover((h) => h ?? Math.max(0, selIndex))}
      onBlur={() => setHover(null)}
    >
      <svg width={width} height={height} className={s.svg} aria-hidden="true">
        {g.yTicks.map((t) => (
          <g key={t}>
            <line className={s.grid} x1={PAD.left} x2={width - PAD.right} y1={g.sy(t)} y2={g.sy(t)} />
            <text className={s.tick} x={PAD.left - 8} y={g.sy(t)} textAnchor="end" dominantBaseline="middle">
              {formatTick(t)}
            </text>
          </g>
        ))}
        {ticks.map((t, i) => (
          <text key={t} className={s.tick} x={g.sx(t)} y={height - 8} textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'}>
            {formatX(t)}
          </text>
        ))}
        {markers.map((m, i) => (
          <g key={m.x} className={m.muted ? s.mutedMarker : s.marker}>
            <line x1={g.sx(m.x)} x2={g.sx(m.x)} y1={PAD.top - 4} y2={PAD.top + g.ih} />
            <text x={g.sx(m.x)} y={i % 2 ? PAD.top - 20 : PAD.top - 8} textAnchor={g.sx(m.x) > width - 90 ? 'end' : g.sx(m.x) < PAD.left + 50 ? 'start' : 'middle'}>
              {m.label}
            </text>
          </g>
        ))}
        {refLine && (
          <g className={s.ref}>
            <line x1={PAD.left} x2={width - PAD.right} y1={g.sy(refLine.y)} y2={g.sy(refLine.y)} />
            <text x={width - PAD.right} y={g.sy(refLine.y) - 6} textAnchor="end">
              {refLine.label}
            </text>
          </g>
        )}
        <path className={s.line} d={g.path} />
        {sp && (
          <g>
            <circle className={s.dot} cx={g.sx(sp.x)} cy={g.sy(sp.y)} r={4.5} />
            <text className={s.dotLabel} x={g.sx(sp.x)} y={g.sy(sp.y) - 12} textAnchor={g.sx(sp.x) > width - 80 ? 'end' : 'middle'}>
              {formatY(sp.y)}
            </text>
          </g>
        )}
        {hp && (
          <g>
            <line className={s.cross} x1={g.sx(hp.x)} x2={g.sx(hp.x)} y1={PAD.top} y2={PAD.top + g.ih} />
            <circle className={s.dot} cx={g.sx(hp.x)} cy={g.sy(hp.y)} r={4.5} />
          </g>
        )}
        <rect className={s.hit} x={PAD.left} y={PAD.top} width={g.iw} height={g.ih} onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
      </svg>
      {hp && (
        <div className={s.tip} style={{ left: tipLeft, top: PAD.top }} role="status">
          <span className={s.tipX}>{formatX(hp.x)}</span>
          <span className={s.tipRow}>
            <i className={s.key} />
            <b>{formatY(hp.y)}</b>
            <span>{seriesLabel}</span>
          </span>
          {refLine && (
            <span className={s.tipRow}>
              <i className={s.keyRef} />
              <b>{formatY(refLine.y)}</b>
              <span>{refLine.label}</span>
            </span>
          )}
        </div>
      )}
    </div>
  )
}
